import { createContext, useContext, useEffect, useRef, memo } from 'react';
import compose, { composeStable } from '../helpers/compose';
import { makeProvider, normalize } from '../helpers/makeContexts';
import {
  useDashboardDispatch,
  DashboardProvider,
} from './single-dashboard/DashboardContext';
import {
  useDashboardChartsDispatch,
  DashboardChartsProvider,
} from './single-dashboard/DashboardChartsContext';
import { DashboardModalProvider } from './single-dashboard/DashboardModalsContext';
import {
  DashboardDataTablesProvider,
  useDashboardDataTablesDispatch,
} from './single-dashboard/DashboardDataTablesContext';
import {
  useDashboardCategoriesDispatch,
  DashboardCategoriesProvider,
} from './single-dashboard/DashboardCategoriesContext';
import {
  useDashboardViewsFiltersDispatch,
  DashboardViewsFiltersProvider,
} from './single-dashboard/DashboardViewsFiltersContext';
import useGetCharts from '../apiCalls/charts';
import { DashChartTabsProvider } from './single-dashboard/DashChartTabsContext';
import DragAndDropProvider from './single-dashboard/DragAndDropProvider';
import generateLocalFilters from './single-dashboard/generateLocalFiltersHelper';
import { useAccountDataTableStore } from './AccountDataTableContext';
import { useSampleDataTableStore } from './SampleDataTableContext';
import convertDataTables from '../helpers/convertDataTables';
import { HydrationProvider, useHydration } from './HydrationContext';

export const [
  DashboardSlotsGlobalProvider, //a global provider of all the slot contexts
  useUpdateLoaderData, //a hook to use in the LocalProvider to get the dispatch of that slot's data context (for saving) - see dataReducer
  DashboardSlotA,
] = createDashboardSlots(['A']);

// slot context is for use within the local provider
const SlotContext = createContext();
// useSlotKey returns the key of the slot you are in when in the local provider (i.e. "A")
export function useSlotKey() {
  return useContext(SlotContext);
}

function createDashboardSlots(keys) {
  const allProviders = [];

  // the configuration for the AssignedDashboard context
  const assignedDashboardInit = {
    id: null,
    isLoading: false,
    isLoadingDashCharts: false,
    isLoadingDataTables: false,
    isLoadingCategories: false,
    isLoadingViewsFilters: false,
  };

  // the reducer for the AssignedDashboard dispatch
  const assignedDashboardReducer = (draft, { type, payload }) => {
    switch (type) {
      case 'startLoading': {
        draft.isLoading = true;
        break;
      }
      case 'setDashboardId': {
        draft.id = payload;
        break;
      }
      case 'setDashboardIdAndStartLoading': {
        draft.id = payload;
        draft.isLoading = true;
        break;
      }
      case 'stopLoading': {
        draft.isLoading = false;
        break;
      }
      case 'startDashboardDataTablesLoading': {
        draft.isLoadingDataTables = true;
        break;
      }
      case 'stopDashboardDataTablesLoading': {
        draft.isLoadingDataTables = false;
        break;
      }
      case 'startDashboardChartsLoading': {
        draft.isLoadingDashCharts = true;
        break;
      }
      case 'stopDashboardChartsLoading': {
        draft.isLoadingDashCharts = false;
        break;
      }
      case 'startDashboardCategoriesLoading': {
        draft.isLoadingCategories = true;
        break;
      }
      case 'stopDashboardCategoriesLoading': {
        draft.isLoadingCategories = false;
        break;
      }
      case 'startDashboardViewsFiltersLoading': {
        draft.isLoadingViewsFilters = true;
        break;
      }
      case 'stopDashboardViewsFiltersLoading': {
        draft.isLoadingViewsFilters = false;
        break;
      }
      default: {
        return null;
      }
    }
  };

  // the configuration for the Data context
  const dataInit = {
    dashboard: {},
    dataTables: {},
    categories: {},
    dashboardCharts: {},
    filters: {},
    views: [],
    activeFilters: {},
    activeViewId: null,
    stageView: {},
    stageFilters: {},
    studentIdCache: {},
    cubeApi: null,
    isLoadingDashCharts: false,
  };

  // the reducer for the Data dispatch
  const dataReducer = (draft, { type, payload }) => {
    switch (type) {
      case 'setAllDashboardData': {
        // add description
        let {
          id,
          title,
          description,
          Categories,
          DashboardUserSettings,
          DashboardDataTables,
          isTemplate,
          createdAt,
          updatedAt,
          AccountId,
          TemplateId,
          UserId,
          isPublishedToMarketplace,
          imgUrl,
          uniqueHash,
          embedUrl,
          AccountDashboardPermissionId,
          layout,
          trashDate,
          filterSettings,
          widgetGrouping,
          useDemoData,
        } = payload;

        draft.dashboard = {
          id,
          title,
          description,
          Categories,
          DashboardUserSettings,
          DashboardDataTables,
          isTemplate,
          createdAt,
          updatedAt,
          AccountId,
          TemplateId,
          UserId,
          isPublishedToMarketplace,
          imgUrl,
          uniqueHash,
          embedUrl,
          AccountDashboardPermissionId,
          layout: layout || { rows: [] },
          trashDate,
          filterSettings,
          widgetGrouping,
          useDemoData,
        };

        break;
      }
      case 'setDashboardDataTablesData': {
        draft.dataTables = normalize(payload);
        break;
      }
      case 'setDashboardChartsData': {
        if (Array.isArray(payload)) {
          draft.dashboardCharts = normalize(payload);
        } else {
          draft.dashboardCharts = payload || {};
        }
        break;
      }
      case 'setDashboardCategoriesData': {
        draft.categories = normalize(payload, 'CategoryId');
        break;
      }
      case 'setIsLoadingDashCharts': {
        draft.isLoadingDashCharts = payload;
        break;
      }
      case 'updateDashboard': {
        draft.dashboard = payload;
        break;
      }
      case 'updateAccDashPermId': {
        draft.dashboard.AccountDashboardPermissionId = payload;
        break;
      }
      case 'updateDUSinDashboard': {
        payload.forEach((dus) => {
          draft.dashboard.DashboardUserSettings[dus.id] = dus;
        });
        break;
      }
      case 'updateDashCats': {
        draft.categories = normalize(payload);
        break;
      }
      case 'updateDataTables': {
        draft.dataTables = payload;
        break;
      }
      case 'addDataTable': {
        draft.dataTables[payload.id] = payload;
        break;
      }
      case 'removeDataTable': {
        delete draft.dataTables[payload.id];
        break;
      }
      case 'updateDashboardCharts': {
        if (Array.isArray(payload)) {
          draft.dashboardCharts = normalize(payload);
        } else {
          draft.dashboardCharts = payload || {};
        }
        break;
      }
      case 'updateDashboardChartsSeq': {
        for (let i = 0; i < payload.DashboardCharts.length; i++) {
          const dashChart = payload.DashboardCharts[i];
          draft.dashboardCharts[dashChart.id] = dashChart;
        }
        break;
      }
      case 'deleteDashboardChart': {
        delete draft.dashboardCharts[payload.dashboardChartId];
        break;
      }
      case 'addDashboardCharts': {
        //this is called when a chart is dropped on the dashboard
        // draft.dashboardCharts[`new${payload?.ChartId}`] = payload;
        for (let i = 0; i < payload.DashboardCharts.length; i++) {
          let dashChart = payload?.DashboardCharts[i];
          let chart = payload?.Charts?.length
            ? payload?.Charts.find((chart) => chart.id === dashChart.ChartId)
            : null;

          const newChart = {
            ...chart,
            ChartDashboardDataTables: chart?.ChartDashboardDataTables || [],
            WidgetCustomJoins: chart?.WidgetCustomJoins || [],
          };
          const newDashChart = {
            ...dashChart,
            Chart: newChart,
          };
          dashChart.Chart = chart;
          draft.dashboardCharts[dashChart.id] = newDashChart;
        }
        break;
      }
      case 'updateChartinDashChart': {
        let dashchartKey = Object.keys(draft.dashboardCharts).find(
          (key) => draft.dashboardCharts[key].ChartId === payload.id
        );

        if (dashchartKey !== undefined) {
          draft.dashboardCharts[[dashchartKey]].Chart = payload;
        }
        break;
      }
      case 'setViewsAndFilters': {
        const {
          views,
          filters,
          activeFilters,
          activeViewId,
          stageView,
          stageFilters,
          studentIdCache,
        } = payload;
        draft.views = views;
        draft.filters = filters;
        draft.activeFilters = activeFilters;
        draft.activeViewId = activeViewId;
        draft.stageView = stageView;
        draft.stageFilters = stageFilters;
        draft.studentIdCache = studentIdCache;
        break;
      }
      case 'clearAllData': {
        draft.dashboard = {};
        draft.dataTables = {};
        draft.categories = {};
        draft.dashboardCharts = {};
        draft.filters = {};
        draft.views = [];
        draft.activeFilters = {};
        draft.activeViewId = null;
        draft.stageView = {};
        draft.stageFilters = {};
        draft.studentIdCache = {};
        draft.cubeApi = null;
        draft.isLoadingDashCharts = false;
        break;
      }
      default: {
        return null;
      }
    }
  };

  // function to create a single DashboardSlot object
  function createDashboardSlot(key) {
    const AssignedDashboardContext = createContext();
    const AssignedDashboardDispatch = createContext();
    const DataContext = createContext();
    const DataDispatch = createContext();

    // Global providers for this slot
    const GlobalProvider = compose([
      makeProvider(
        AssignedDashboardContext,
        AssignedDashboardDispatch,
        assignedDashboardReducer,
        assignedDashboardInit
      ),
      makeProvider(DataContext, DataDispatch, dataReducer, dataInit),
    ]);
    allProviders.push(GlobalProvider);

    // Stable composed local provider (built once per slot)
    const DashboardLocalProvider = composeStable([
      HydrationProvider,
      DashboardProvider,
      DashChartTabsProvider,
      DashboardChartsProvider,
      DashboardModalProvider,
      DashboardDataTablesProvider,
      DashboardCategoriesProvider,
      DragAndDropProvider,
      DashboardViewsFiltersProvider,
      ({ children }) => (
        <SlotContext.Provider value={key}>{children}</SlotContext.Provider>
      ),
    ]);

    // Local helpers (in scope for LocalLoader)
    const useAssignedDashboard = () => useContext(AssignedDashboardContext);
    const useDataContext = () => useContext(DataContext);

    const LocalLoader = () => {
      const { setHydratedFor } = useHydration();
      const {
        id: dashboardId,
        isLoading,
        isLoadingDashCharts,
        isLoadingDataTables,
        isLoadingCategories,
        isLoadingViewsFilters,
      } = useAssignedDashboard();

      const {
        dashboard,
        dataTables,
        categories,
        dashboardCharts,
        views,
        filters,
        activeFilters,
        activeViewId,
        studentIdCache,
      } = useDataContext();

      const bootstrappedFor = useRef(null);

      const dashboardEditDispatch = useDashboardDispatch();
      const dashChartsDispatch = useDashboardChartsDispatch();
      const dashboardDataDispatch = useDashboardDataTablesDispatch();
      const dashboardCatDispatch = useDashboardCategoriesDispatch();
      const dashboardViewsFiltersDispatch = useDashboardViewsFiltersDispatch();

      const { accountdatatables } = useAccountDataTableStore();
      const { sampledatatables } = useSampleDataTableStore();

      // If this hook is cheap/safe, it can run; otherwise move where appropriate
      useGetCharts();

      const allLoaded =
        !!dashboardId &&
        !isLoading &&
        !isLoadingDashCharts &&
        !isLoadingDataTables &&
        !isLoadingCategories &&
        !isLoadingViewsFilters;

      useEffect(() => {
        const formattedData = convertDataTables(
          Object.values(dataTables || {}).reduce((acc, dt) => {
            acc[dt.id] = dt;
            return acc;
          }, {}),
          accountdatatables,
          sampledatatables
        );

        // Seed locals once
        dashboardEditDispatch({
          type: 'setEditedDashboard',
          payload: dashboard,
        });
        dashboardDataDispatch({
          type: 'setDataTables',
          payload: formattedData,
        });
        dashboardCatDispatch({
          type: 'setDashCategories',
          payload: categories,
        });

        dashboardViewsFiltersDispatch({ type: 'setViews', payload: views });
        dashboardViewsFiltersDispatch({ type: 'setFilters', payload: filters });
        dashboardViewsFiltersDispatch({
          type: 'setActiveFilters',
          payload: activeFilters,
        });
        dashboardViewsFiltersDispatch({
          type: 'setActiveViewId',
          payload: activeViewId,
        });
        dashboardViewsFiltersDispatch({
          type: 'setStageView',
          payload: (views || []).find((v) => v.id === activeViewId) || {},
        });
        dashboardViewsFiltersDispatch({
          type: 'setStageFilters',
          payload: activeFilters,
        });
        dashboardViewsFiltersDispatch({
          type: 'setStudentIdCache',
          payload: studentIdCache,
        });

        dashboardEditDispatch({
          type: 'setWidgets',
          payload: dashboard?.widgetGrouping,
        });
        dashboardEditDispatch({
          type: 'setWidgetGrouping',
          payload: dashboard?.widgetGrouping,
        });

        if (dashboard?.widgetGrouping?.length) {
          dashboardEditDispatch({
            type: 'setLocalFilters',
            payload: generateLocalFilters(dashboard.widgetGrouping),
          });
        }

        // Charts seeded once
        dashChartsDispatch({
          type: 'setDashboardCharts',
          payload: dashboardCharts,
        });
        setHydratedFor(dashboardId);
        bootstrappedFor.current = dashboardId;
      }, [allLoaded, dashboardId, dashboard, dashboardCharts]);

      return null;
    };

    const LocalProvider = memo(function LocalProvider({ children }) {
      return (
        <DashboardLocalProvider>
          <LocalLoader />
          {children}
        </DashboardLocalProvider>
      );
    });

    return {
      key,
      useAssignedDashboard,
      useAssignedDashboardDispatch: () => useContext(AssignedDashboardDispatch),
      useDataContext,
      useDataDispatch: () => useContext(DataDispatch),
      LocalProvider,
    };
  }

  const allDashboardSlots = keys.map(createDashboardSlot);

  // a function to use whichever data dispatch goes with the LocalLoader
  function useUpdateLoaderData() {
    // single-slot optimization; avoids calling hooks in a loop
    return allDashboardSlots[0].useDataDispatch();
  }
  return [compose(allProviders), useUpdateLoaderData, ...allDashboardSlots];
}
